import React, { useState, useRef, useEffect } from 'react'
import { Box, Flex, Text, Input } from '@chakra-ui/react'
import Icon from '../shared/icons'

interface FilterOption {
	name: string
	value: string
}

interface FilterProps {
	filters: FilterOption[]
	label?: string
	setFilter?: (value: any) => void
	placeholder?: string
	maxHeight?: string
	showSearch?: boolean
}

const Filter: React.FC<FilterProps> = ({
	filters,
	label = 'Estado:',
	setFilter,
	placeholder = 'Seleccionar...',
	maxHeight = '200px',
	showSearch = true,
}) => {
	const [selected, setSelected] = useState(filters?.[0]?.value || '')
	const [isOpen, setIsOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const dropdownRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)

	const selectedName =
		filters.find((item) => item.value === selected)?.name || placeholder

	const filteredOptions = filters.filter((option) =>
		option.name.toLowerCase().includes(searchTerm.toLowerCase())
	)

	const handleSelect = (value: string) => {
		setSelected(value)
		setFilter?.(value)
		setIsOpen(false)
		setSearchTerm('')
	}

	const handleToggle = () => {
		setIsOpen(!isOpen)
		if (!isOpen) {
			setSearchTerm('')
			setTimeout(() => {
				if (searchInputRef.current && showSearch) {
					searchInputRef.current.focus()
				}
			}, 100)
		}
	}

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
				setSearchTerm('')
			}
		}

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false)
				setSearchTerm('')
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [])

	const getDropdownPosition = () => {
		if (!dropdownRef.current) return { top: 'calc(100% + 8px)', bottom: 'auto' }

		const rect = dropdownRef.current.getBoundingClientRect()
		const viewportHeight = window.innerHeight
		const spaceBelow = viewportHeight - rect.bottom
		const dropdownHeight = parseInt(maxHeight) + 60

		if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
			return { top: 'auto', bottom: 'calc(100% + 8px)' }
		}

		return { top: 'calc(100% + 8px)', bottom: 'auto' }
	}

	const dropdownPosition = isOpen
		? getDropdownPosition()
		: { top: 'calc(100% + 8px)', bottom: 'auto' }

	return (
		<Box
			w={{ base: 'full', sm: 'auto' }}
			display='flex'
			alignItems='center'
			p='3'
			bg='white'
			borderRadius='md'
			boxShadow='0px 1px 3px rgba(0, 0, 0, 0.05)'
			gap='4'
			position='relative'
			transition='all 0.2s'
			_hover={{ boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.08)' }}
		>
			<Text
				fontWeight='600'
				color='gray.700'
				fontSize='sm'
				letterSpacing='tight'
			>
				{label}
			</Text>

			<Box position='relative' ref={dropdownRef} zIndex={isOpen ? 20 : 1}>
				<Flex
					alignItems='center'
					cursor='pointer'
					onClick={handleToggle}
					position='relative'
					minW='120px'
				>
					<Text
						fontSize='sm'
						fontWeight='500'
						color={selected ? 'gray.800' : 'gray.500'}
						mr='2'
						overflow='hidden'
						textOverflow='ellipsis'
						whiteSpace='nowrap'
					>
						{selectedName}
					</Text>

					<Box
						transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
						transition='transform 0.2s'
						display='flex'
						alignItems='center'
					>
						<Icon icon='chevronDown' color='gray.600' size='lg' />
					</Box>
				</Flex>

				{isOpen && (
					<Box
						position='absolute'
						top={dropdownPosition.top}
						bottom={dropdownPosition.bottom}
						left='0'
						right='0'
						bg='white'
						borderRadius='lg'
						boxShadow='xl'
						overflow='hidden'
						minW='150px'
						border='1px solid'
						borderColor='gray.200'
						zIndex={2}
					>
						{showSearch && filters.length > 5 && (
							<Box p='3' borderBottom='1px solid' borderColor='gray.100'>
								<Input
                className='p-2'
									ref={searchInputRef}
									placeholder='Buscar opciones...'
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									size='sm'
									borderRadius='md'
									_focus={{
										borderColor: 'blue.500',
										boxShadow: '0 0 0 1px blue.500',
									}}
								/>
							</Box>
						)}

						<Box
							maxH={maxHeight}
							overflowY='auto'
							css={{
								'&::-webkit-scrollbar': {
									width: '6px',
								},
								'&::-webkit-scrollbar-track': {
									background: 'transparent',
								},
								'&::-webkit-scrollbar-thumb': {
									background: '#CBD5E0',
									borderRadius: '3px',
								},
								'&::-webkit-scrollbar-thumb:hover': {
									background: '#A0AEC0',
								},
							}}
						>
							{filteredOptions.length > 0 ? (
								<Flex direction='column'>
									{filteredOptions.map((item, index) => (
										<Box
											key={`${item.value}-${index}`}
											py='3'
											px='4'
											bg={selected === item.value ? 'blue.50' : 'white'}
											_hover={{
												bg: selected === item.value ? 'blue.100' : 'gray.50',
											}}
											cursor='pointer'
											transition='background 0.2s'
											onClick={() => handleSelect(item.value)}
											fontSize='sm'
											fontWeight={selected === item.value ? '600' : '400'}
											color={selected === item.value ? 'blue.700' : 'gray.700'}
											borderBottom={
												index < filteredOptions.length - 1
													? '1px solid'
													: 'none'
											}
											borderColor='gray.100'
											position='relative'
										>
											<Flex align='center' justify='space-between'>
												<Text
													overflow='hidden'
													textOverflow='ellipsis'
													whiteSpace='nowrap'
													flex='1'
												>
													{item.name}
												</Text>
												{selected === item.value && (
													<Icon icon='check' color='blue.500' size='sm' />
												)}
											</Flex>
										</Box>
									))}
								</Flex>
							) : (
								<Box p='4' textAlign='center' color='gray.500' fontSize='sm'>
									<Flex direction='column' align='center' gap='2'>
										<Icon icon='search' color='gray.400' size='lg' />
										<Text>No se encontraron opciones</Text>
									</Flex>
								</Box>
							)}
						</Box>

						{filteredOptions.length > 10 && (
							<Box
								p='2'
								borderTop='1px solid'
								borderColor='gray.100'
								bg='gray.50'
								textAlign='center'
							>
								<Text fontSize='xs' color='gray.500'>
									{filteredOptions.length} opciones disponibles
								</Text>
							</Box>
						)}
					</Box>
				)}

				<select
					value={selected}
					onChange={(e) => handleSelect(e.target.value)}
					style={{
						position: 'absolute',
						opacity: 0,
						pointerEvents: 'none',
						height: 0,
						width: 0,
						overflow: 'hidden',
					}}
					aria-hidden='true'
					tabIndex={-1}
				>
					{filters.map((item, index) => (
						<option key={`option-${index}`} value={item.value}>
							{item.name}
						</option>
					))}
				</select>
			</Box>
		</Box>
	)
}

export default Filter
